import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_RAW_RETAILER_ASSOCIATION,
  GET_MASTER_DATA_RAW_RETAILER_ASSOCIATIONS,
  UPDATE_MASTER_DATA_RAW_RETAILER_ASSOCIATION,
} from "@src/domain/graphql/masterData";
import {
  MasterDataRawRetailerAssociation,
  type IMasterDataRawRetailerAssociation,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataRawRetailerAssociationDTO,
  GetMasterDataRawRetailerAssociationsDTO,
  UpdateMasterDataRawRetailerAssociationDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataRawRetailerAssociationRepository,
  MasterDataRawRetailerAssociationsListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapRawAssociationItem(
  item: IMasterDataRawRetailerAssociation,
): MasterDataRawRetailerAssociation {
  return MasterDataRawRetailerAssociation.from({
    rawRetailerId: item.rawRetailerId,
    rawRetailerName: item.rawRetailerName,
    retailerId: item.retailerId,
    retailerName: item.retailerName ?? null,
  });
}

export class MasterDataRawRetailerAssociationRepository
  implements IMasterDataRawRetailerAssociationRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getRawAssociations(
    params: GetMasterDataRawRetailerAssociationsDTO,
  ): Promise<MasterDataRawRetailerAssociationsListResult> {
    const { data, error } = await this.client.query<{
      masterDataRawRetailerAssociations: {
        items: IMasterDataRawRetailerAssociation[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_RAW_RETAILER_ASSOCIATIONS,
      variables: {
        params: {
          search: params.search ?? null,
          retailerId: params.retailerId ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataRawRetailerAssociations;
    if (!response) {
      throw new Error("Failed to fetch master data raw retailer associations");
    }

    return toMasterDataListResult(
      response,
      mapRawAssociationItem,
      params.page_number,
      params.page_size,
    );
  }

  async createRawAssociation(
    params: CreateMasterDataRawRetailerAssociationDTO,
  ): Promise<MasterDataRawRetailerAssociation> {
    return this.putRawAssociation(
      CREATE_MASTER_DATA_RAW_RETAILER_ASSOCIATION,
      {
        retailerId: params.retailerId,
        rawRetailerName: params.rawRetailerName,
      },
      (data) => data?.createMasterDataRawRetailerAssociation,
      "Failed to create raw retailer association",
    );
  }

  async updateRawAssociation(
    params: UpdateMasterDataRawRetailerAssociationDTO,
  ): Promise<MasterDataRawRetailerAssociation> {
    return this.putRawAssociation(
      UPDATE_MASTER_DATA_RAW_RETAILER_ASSOCIATION,
      {
        rawRetailerId: params.rawRetailerId,
        rawRetailerName: params.rawRetailerName,
      },
      (data) => data?.updateMasterDataRawRetailerAssociation,
      "Failed to update raw retailer association",
    );
  }

  private async putRawAssociation(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataRawRetailerAssociation?: IMasterDataRawRetailerAssociation;
            updateMasterDataRawRetailerAssociation?: IMasterDataRawRetailerAssociation;
          }
        | null
        | undefined,
    ) => IMasterDataRawRetailerAssociation | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataRawRetailerAssociation> {
    const result = await this.client.mutate<{
      createMasterDataRawRetailerAssociation?: IMasterDataRawRetailerAssociation;
      updateMasterDataRawRetailerAssociation?: IMasterDataRawRetailerAssociation;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapRawAssociationItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
